/* eslint-disable @typescript-eslint/no-explicit-any -- Data preview operates on dynamic CSV row shapes; prefer explicit typing in a dedicated cleanup. */
import React, { useMemo } from 'react';
// Components
import {
  Alert,
  Badge,
  Button,
  Callout,
  EmptyContent,
  Flex,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import {
  IconAlertTriangle,
  IconCheck,
  IconEye,
  IconFileImport,
  IconFolderOpen,
  IconInfoCircle,
  IconShieldCheck,
} from '@tabler/icons-react';

// Utils
import { ColumnDef } from '@tanstack/react-table';

// Types
import {
  ColumnMapping,
  FileToFormMapping,
  SelectedFile,
  StudyForm,
} from './types';
import { FormFieldHoverCard } from '@/components/data-import/FormFieldHoverCard';
import { extractPathFromNestedPath, matchesNestedPath } from './utils/formTreeUtils';
import { getFormDisplayName } from '@/utils/formUtils';

interface DataPreviewTableProps {
  selectedForm?: StudyForm;
  selectedFile?: SelectedFile;
  selectedFileId?: string;
  selectedFormNestedPath?: string;
  forms: StudyForm[];
  fileToFormMappings: FileToFormMapping[];
  columnMappings: ColumnMapping[];
  selectedFiles: SelectedFile[];
  onValidateData?: () => void;
  isValidating?: boolean;
  validationResults?: any;
}

export const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  selectedForm,
  selectedFile,
  selectedFileId,
  selectedFormNestedPath,
  forms,
  fileToFormMappings,
  columnMappings,
  selectedFiles,
  onValidateData,
  isValidating,
  validationResults,
}) => {
  // Get files mapped directly to the selected form (NOT including child forms)
  // If selectedFormNestedPath is provided, filter files to match that path
  const formFiles = useMemo(() => {
    if (!selectedForm) return [];

    // Get all files mapped to this form
    let mappings = fileToFormMappings.filter(
      mapping => mapping.formId === selectedForm.id
    );

    // If selectedFormNestedPath is provided, filter files with nested_path to match that path
    // Files without nested_path are always included (they are mapped directly to the form)
    if (selectedFormNestedPath) {
      mappings = mappings.filter(mapping => {
        // If file has nested_path, it must match selectedFormNestedPath
        if (mapping.nested_path) {
          // Extract path from selectedFormNestedPath
          const selectedPath = extractPathFromNestedPath(selectedFormNestedPath);
          return matchesNestedPath(selectedPath, mapping.nested_path);
        }
        // If file doesn't have nested_path, always include it (mapped directly to the form)
        return true;
      });
    }

    return mappings.map(mapping => {
      return {
        mapping,
        fileId: mapping.fileId,
      };
    });
  }, [selectedForm, fileToFormMappings, selectedFormNestedPath]);

  // Generate preview data for form view
  const formPreviewData = useMemo(() => {
    if (!selectedForm) {
      return null;
    }

    const formFieldMappings = new Map<string, ColumnMapping[]>();

    // Collect all mappings for this form only (not descendants)
    formFiles.forEach(({ fileId }) => {
      const fileMappings = columnMappings.filter(
        cm => cm.fileId === fileId && cm.mappingType === 'formField'
      );
      fileMappings.forEach(fileMapping => {
        if (!fileMapping.targetField) return;
        const targetField = fileMapping.targetField;
        if (!formFieldMappings.has(targetField)) {
          formFieldMappings.set(targetField, []);
        }
        formFieldMappings.get(targetField)!.push(fileMapping);
      });
    });

    // Get sample data from actual files (first 10 rows combined)
    let sampleRows: Record<string, any>[] = [];
    let totalRows = 0;

    formFiles.forEach(({ fileId }) => {
      const file = selectedFiles.find(f => {
        const fid =
          f.source === 'uploaded' ? f.file!.name : `csv-${f.csvFile!.id}`;
        return fid === fileId;
      });

      if (file) {
        // Count total rows from this file
        totalRows += file.metadata.rowCount;

        // Try to get sample data from file
        let fileSampleData: any[] = [];
        let csvHeaders: string[] = [];

        if (file.source === 'csv-importer' && file.headerData) {
          // Use real sample data from CSV importer
          fileSampleData = file.headerData.first_10_rows;
          csvHeaders = file.headerData.headers;
        } else if (file.source === 'uploaded' && file.headerData) {
          // Use sample data from uploaded file
          fileSampleData = file.headerData.first_10_rows;
          csvHeaders = file.headerData.headers;
        }

        // Fix: If first_10_rows contains strings instead of arrays, parse them
        if (fileSampleData.length > 0 && typeof fileSampleData[0] === 'string') {
          fileSampleData = fileSampleData.map((row: any) => {
            if (typeof row === 'string') {
              // Parse CSV line manually (simple version - handles quoted values)
              const result: string[] = [];
              let current = '';
              let inQuotes = false;
              
              for (let i = 0; i < row.length; i++) {
                const char = row[i];
                const nextChar = row[i + 1];
                
                if (char === '"') {
                  if (inQuotes && nextChar === '"') {
                    current += '"';
                    i++; // Skip next quote
                  } else {
                    inQuotes = !inQuotes;
                  }
                } else if (char === ',' && !inQuotes) {
                  result.push(current.trim());
                  current = '';
                } else {
                  current += char;
                }
              }
              
              // Add last field
              result.push(current.trim());
              return result;
            }
            return row;
          });
        }

        if (fileSampleData.length > 0) {
          // Map file data to form fields
          const fileMappings = columnMappings.filter(
            cm => cm.fileId === fileId && cm.mappingType === 'formField'
          );

          const fileRows = fileSampleData.map((rowData, index) => {
            const row: Record<string, any> = {
              id: `file-${fileId}-row-${index + 1}`,
            };

            selectedForm.fields?.forEach(field => {
              const mapping = fileMappings.find(
                m => m.targetField === field.dataPoint
              );
              
              if (mapping) {
                // Get value from row data by column name
                const columnIndex = csvHeaders.indexOf(mapping.columnName);
                
                if (columnIndex >= 0 && columnIndex < rowData.length) {
                  row[field.dataPoint] = rowData[columnIndex];
                } else {
                  row[field.dataPoint] = '⚠️ Column not found';
                }
              } else {
                row[field.dataPoint] = field.required
                  ? '⚠️ Required field not mapped'
                  : 'Not mapped';
              }
            });

            return row;
          });

          sampleRows.push(...fileRows);
        }
      }
    });

    // If no files are mapped to this form, return empty table with headers
    if (formFiles.length === 0) {
      return {
        columns:
          selectedForm.fields?.map(field => ({
            key: field.dataPoint,
            label: field.label || field.dataPoint,
            required: field.required,
            type: field.type,
            isMapped: false,
          })) || [],
        rows: [],
        totalRows: 0,
        mappedFields: 0,
        totalFields: selectedForm.fields?.length || 0,
      };
    }

    // If no sample data available, generate mock data (up to 10 rows)
    if (sampleRows.length === 0) {
      const mockRowsCount = Math.min(10, Math.max(1, totalRows)); // Generate at least 1 row, up to 10, or match totalRows if less than 10
      sampleRows = Array.from({ length: mockRowsCount }, (_, index) => {
        const row: Record<string, any> = { id: `mock-row-${index + 1}` };

        selectedForm.fields?.forEach(field => {
          const mappings = formFieldMappings.get(field.dataPoint);
          if (mappings && mappings.length > 0) {
            const mapping = mappings[0];
            let value = `${mapping.columnName}_row_${index + 1}`;

            switch (field.type) {
              case 'date':
                value = `2024-01-${String(index + 1).padStart(2, '0')}`;
                break;
              case 'number':
                value = (Math.random() * 1000).toFixed(2);
                break;
              case 'radio':
              case 'select':
                if (field.allowedValues && field.allowedValues.length > 0) {
                  value =
                    field.allowedValues[index % field.allowedValues.length];
                }
                break;
              default:
                value = `${mapping.columnName} data ${index + 1}`;
            }
            row[field.dataPoint] = value;
          } else {
            row[field.dataPoint] = field.required
              ? '⚠️ Required field not mapped'
              : 'Not mapped';
          }
        });

        return row;
      });
    }

    return {
      columns:
        selectedForm.fields?.map(field => ({
          key: field.dataPoint,
          label: field.label || field.dataPoint,
          required: field.required,
          type: field.type,
          isMapped: formFieldMappings.has(field.dataPoint),
        })) || [],
      rows: sampleRows,
      totalRows: totalRows,
      mappedFields: formFieldMappings.size,
      totalFields: selectedForm.fields?.length || 0,
    };
  }, [selectedForm, formFiles, columnMappings, selectedFiles]);

  // Generate preview data for file view
  const filePreviewData = useMemo(() => {
    if (!selectedFile || !selectedFileId || !selectedForm) {
      return null;
    }

    const fileMappings = columnMappings.filter(
      cm => cm.fileId === selectedFileId
    );

    // Get sample data from file
    let sampleData = selectedFile.headerData?.first_10_rows || [];
    const csvHeaders = selectedFile.headerData?.headers || [];

    // Fix: If first_10_rows contains strings instead of arrays, parse them
    if (sampleData.length > 0 && typeof sampleData[0] === 'string') {
      sampleData = sampleData.map((row: any) => {
        if (typeof row === 'string') {
          // Parse CSV line manually (simple version - handles quoted values)
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            const nextChar = row[i + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                current += '"';
                i++; // Skip next quote
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          
          // Add last field
          result.push(current.trim());
          return result;
        }
        return row;
      });
    }

    let sampleRows: Record<string, any>[] = [];

    if (sampleData.length > 0) {
      // Use real sample data from CSV and map to form fields
      sampleRows = sampleData.map((rowData, index) => {
        const row: Record<string, any> = { id: `row-${index + 1}` };

        selectedForm.fields?.forEach(field => {
          const mapping = fileMappings.find(
            m =>
              m.targetField === field.dataPoint && m.mappingType === 'formField'
          );
          if (mapping) {
            // Get value from CSV row by column name
            const columnIndex = csvHeaders.indexOf(mapping.columnName);
            
            if (columnIndex >= 0 && columnIndex < rowData.length) {
              row[field.dataPoint] = rowData[columnIndex];
            } else {
              row[field.dataPoint] = '⚠️ Column not found';
            }
          } else {
            row[field.dataPoint] = field.required
              ? '⚠️ Required field not mapped'
              : 'Not mapped';
          }
        });

        return row;
      });
    } else {
      // Generate mock data if no sample data available
      sampleRows = Array.from(
        { length: Math.min(10, selectedFile.metadata.rowCount) },
        (_, index) => {
          const row: Record<string, any> = { id: `mock-row-${index + 1}` };

          selectedForm.fields?.forEach(field => {
            const mapping = fileMappings.find(
              m =>
                m.targetField === field.dataPoint &&
                m.mappingType === 'formField'
            );
            if (mapping) {
              let value = `sample_data_${index + 1}`;
              switch (field.type) {
                case 'date':
                  value = `2024-01-${String(index + 1).padStart(2, '0')}`;
                  break;
                case 'number':
                  value = (Math.random() * 1000).toFixed(2);
                  break;
                case 'radio':
                case 'select':
                  if (field.allowedValues && field.allowedValues.length > 0) {
                    value =
                      field.allowedValues[index % field.allowedValues.length];
                  }
                  break;
              }
              row[field.dataPoint] = value;
            } else {
              row[field.dataPoint] = field.required
                ? '⚠️ Required field not mapped'
                : 'Not mapped';
            }
          });

          return row;
        }
      );
    }

    return {
      columns:
        selectedForm.fields?.map(field => ({
          key: field.dataPoint,
          label: field.label || field.dataPoint,
          required: field.required,
          type: field.type,
          isMapped: fileMappings.some(
            m =>
              m.targetField === field.dataPoint && m.mappingType === 'formField'
          ),
        })) || [],
      rows: sampleRows,
      totalRows: selectedFile.metadata.rowCount,
      mappedFields: fileMappings.filter(m => m.mappingType === 'formField')
        .length,
      totalFields: selectedForm.fields?.length || 0,
    };
  }, [selectedFile, selectedFileId, selectedForm, columnMappings]);

  const previewData = selectedFile ? filePreviewData : formPreviewData;

  // Get column mappings for each field (for hover card info)
  const fieldColumnMappings = useMemo(() => {
    if (!selectedForm) return new Map<string, ColumnMapping>();

    const mappingsMap = new Map<string, ColumnMapping>();

    if (selectedFile && selectedFileId) {
      // File view: get mappings from the selected file
      const fileMappings = columnMappings.filter(
        cm => cm.fileId === selectedFileId && cm.mappingType === 'formField'
      );
      fileMappings.forEach(mapping => {
        if (mapping.targetField) {
          mappingsMap.set(mapping.targetField, mapping);
        }
      });
    } else {
      // Form view: get mappings from all files mapped to this form
      formFiles.forEach(({ fileId }) => {
        const fileMappings = columnMappings.filter(
          cm => cm.fileId === fileId && cm.mappingType === 'formField'
        );
        fileMappings.forEach(mapping => {
          if (mapping.targetField && !mappingsMap.has(mapping.targetField)) {
            // Use first mapping found (in case multiple files map to same field)
            mappingsMap.set(mapping.targetField, mapping);
          }
        });
      });
    }

    return mappingsMap;
  }, [selectedForm, selectedFile, selectedFileId, columnMappings, formFiles]);

  // Generate DataTable columns
  const tableColumns: ColumnDef<Record<string, any>>[] = useMemo(() => {
    if (!previewData || !selectedForm) return [];

    const columns: ColumnDef<Record<string, any>>[] = [
      {
        id: 'rowIndex',
        accessorFn: (row, index) => index + 1,
        header: ({ column: tableColumn }) => (
          <DataTableColumnHeader column={tableColumn} title="#" />
        ),
        meta: {
          cell: {
            variant: 'count',
          },
          size: 60,
        },
        enableSorting: false,
      },
    ];

    previewData.columns.forEach(column => {
      // Find the field from selectedForm to get full field info for hover card
      const field = selectedForm.fields?.find(f => f.dataPoint === column.key);
      // Find column mapping for this field
      const columnMapping = fieldColumnMappings.get(column.key);

      columns.push({
        id: `field-${(column.key || '').toString().replace(/\./g, '-')}`,
        accessorKey: column.key,
        header: ({ column: tableColumn }) => (
          <DataTableColumnHeader
            column={tableColumn}
            title={
              <Flex align="center" gap="1" wrap="wrap">
                <Text size="2" weight="medium">
                  {column.key}
                </Text>
                {column.required && (
                  <Badge color="red" variant="soft" size="xsmall">
                    R
                  </Badge>
                )}
                {!column.isMapped && (
                  <Badge color="gray" variant="outlined" size="xsmall">
                    U
                  </Badge>
                )}
              </Flex>
            }
            infoSlot={
              field ? (
                <FormFieldHoverCard
                  field={field}
                  usePopover={true}
                  columnMapping={
                    columnMapping
                      ? {
                          columnName: columnMapping.columnName,
                          fileId: columnMapping.fileId,
                        }
                      : undefined
                  }
                >
                  <IconInfoCircle
                    size={16}
                    color="var(--blue-9)"
                    style={{
                      cursor: 'pointer',
                      opacity: 0.7,
                      flexShrink: 0,
                    }}
                  />
                </FormFieldHoverCard>
              ) : undefined
            }
          />
        ),
        meta: {
          cell: {
            variant: 'text',
          },
          size: 'auto',
        },
        enableSorting: false,
      });
    });

    return columns;
  }, [previewData, selectedForm, fieldColumnMappings]);

  if (!selectedForm && !selectedFile) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconEye />}
        title="Select a form or file to preview data"
        description={
          <Text variant="inherit" size="2small" align="center">
            Click on a form in the left panel to see combined preview of all
            files mapped to it, <br />
            or click on a specific file to see how it will be imported into its
            target form.
          </Text>
        }
      />
    );
  }

  // If no preview data available, show placeholder
  if (!previewData) {
    return (
      <EmptyContent
        icon={<IconAlertTriangle />}
        title="Generating preview data..."
        description="Please wait while we prepare the preview data."
        size="medium"
      />
    );
  }

  return (
    <Flex direction="column" gap="3" p="4">
      {/* Header */}
      <Flex justify="between" align="center">
        <Flex direction="column" gap="1">
          <Flex align="center" gap="2">
            {selectedFile ? (
              <IconFileImport />
            ) : (
              <IconFolderOpen color="var(--blue-9)" />
            )}
            <Text size="2xsmall" weight="medium">
              {selectedFile ? 'File Preview' : 'Form Preview'}
              <Text size="2xsmall" color="gray" as="span" ml="3">
                {selectedFile ? (
                  <Flex display="inline-flex" direction="row" gap="1">
                    <Badge>
                      {selectedFile.source === 'uploaded'
                        ? selectedFile.file!.name
                        : selectedFile.csvFile!.filename}
                    </Badge>
                    <Badge>→</Badge>
                    <Badge>{selectedForm?.name}</Badge>
                  </Flex>
                ) : (
                  (() => {
                    // Build full path for the form
                    let formPath = selectedForm?.name || '';
                    if (selectedFormNestedPath && selectedForm) {
                      const pathIds = extractPathFromNestedPath(selectedFormNestedPath);
                      if (pathIds.length > 0) {
                        // Get form names for the path
                        const pathNames = pathIds
                          .map(id => {
                            const form = forms.find(f => f.id === id);
                            return form ? getFormDisplayName(form) : null;
                          })
                          .filter((name): name is string => name !== null);
                        
                        if (pathNames.length > 0) {
                          formPath = pathNames.join(' → ');
                        }
                      }
                    }
                    return `${formPath} (${formFiles.length} file${formFiles.length !== 1 ? 's' : ''} mapped directly)`;
                  })()
                )}
              </Text>
            </Text>

            {/* Stats */}
            <Flex gap="3" wrap="wrap">
              {previewData.rows.length > 0 ? (
                <>
                  <Badge color="green" variant="soft">
                    {previewData.rows.length} sample rows
                  </Badge>
                  <Badge color="blue" variant="soft">
                    {previewData.totalRows.toLocaleString()} total rows
                  </Badge>
                  {previewData.mappedFields < previewData.totalFields && (
                    <Badge color="orange" variant="soft">
                      {previewData.totalFields - previewData.mappedFields}{' '}
                      unmapped fields
                    </Badge>
                  )}
                </>
              ) : (
                <Badge color="gray" variant="soft">
                  No files mapped to this form yet
                </Badge>
              )}
            </Flex>
          </Flex>
        </Flex>

        <Flex gap="2" align="center">
          <Badge color="blue" variant="soft">
            {previewData.mappedFields} of {previewData.totalFields} fields
            mapped
          </Badge>
          <VisuallyHidden>
            {onValidateData && (
              <Button
                variant="soft"
                color="primary"
                size="small"
                onClick={onValidateData}
                loading={isValidating}
                startIcon={<IconShieldCheck />}
              >
                {isValidating ? 'Validating...' : 'Dry Run'}
              </Button>
            )}
          </VisuallyHidden>
        </Flex>
      </Flex>

      {/* Validation Results */}
      {validationResults && (
        <Alert color={validationResults.isValid ? 'success' : 'error'}>
          {validationResults.isValid ? <IconCheck /> : <IconAlertTriangle />}
          <Text size="2">
            {validationResults.isValid
              ? 'Validation passed! Ready for import.'
              : 'Validation failed. Check mappings and try again.'}
          </Text>
        </Alert>
      )}

      {/* Preview Table */}
      <DataTable
        withDividers
        data={previewData.rows}
        columns={tableColumns}
        enableSorting={false}
        enableFiltering={false}
        enablePagination={false}
        enableColumnVisibility={false}
        enableRowSelection={false}
        getRowId={(row: any) => row.id}
        emptyMessage={
          previewData.rows.length === 0
            ? 'No files mapped to this form yet. Upload and map files to see preview data.'
            : undefined
        }
      />

      {/* Footer Note */}
      {previewData.rows.length > 0 && (
        <Callout size="small" color="warning" variant="surface">
          {selectedFile
            ? '⚠️ This preview shows how this specific file will be imported into the form.'
            : '⚠️ This preview shows combined data from all files mapped directly to this form (excluding files mapped to child forms).'}
        </Callout>
      )}
    </Flex>
  );
};

export default DataPreviewTable;
